import _ from "lodash";
import React, { useEffect, useState, useRef } from "react";
import classNames from "classnames";
import classes from "./RelatedConceptTable.scss";
import { Table, Tooltip } from "@toddle-edu/ds-web";
import MultiSelectDropdown from "UIComponents/MultiSelectDropdown";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import update from "immutability-helper";
import { getPlannerElementNodeSetsFromIdsQuery } from "UnitPlans/modules/UnitPlanQuery";
import { getRelatedConceptsGroupedValueOptions } from "UnitPlans/modules/UnitPlanModule";
import { isNewPYPConceptsLabelEnabled } from "lib/i18nHelperVariables";
import { useI18n } from "Hooks";
import {
  getOrganizationId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import withDerivedProps from "lib/withDerivedProps";

const styles = {
  overlayInnerStyle: {
    maxWidth: "300px",
    padding: "8px 12px",
    wordBreak: "break-word",
  },
};

const getFormattedLabels = (value, t) => {
  if (!value || _.isEmpty(value)) return t("common:not_selected");
  const showLabelsCount = 1;
  const separator = t("common:and");
  const moreLabel = t("common:more_lowercase");
  const selectedItemsSize = _.size(value);
  const labelsOfSelectedItems = _.map(value, val => {
    return val?.label;
  });
  const labels = _.slice(labelsOfSelectedItems, 0, showLabelsCount).join(", ");
  const count = selectedItemsSize - showLabelsCount;
  const moreText = count > 0 ? `${separator} ${count} ${moreLabel}` : "";

  return `${labels} ${moreText}`;
};

const getLabels = value => {
  return (
    <div>
      {_.map(value, val => (
        <div key={val?.label}>{val?.label}</div>
      ))}
    </div>
  );
};

const getGroupLabel = (groupId, t) => {
  switch (groupId) {
    case "CONCEPT":
      return isNewPYPConceptsLabelEnabled()
        ? t("common:key_concepts_updated")
        : t("common:CONCEPT");
    case "SUBJECT":
      return t("common:subject_plural");
    default:
      return t(`common:${groupId}`);
  }
};

const getRelatedConceptsLabel = t => {
  return isNewPYPConceptsLabelEnabled()
    ? t("common:related_concepts_updated")
    : t("common:related_concepts");
};

const getEmptyStateLabel = (groupId, groupLabel, t) => {
  const getLabelText = () => {
    switch (groupId) {
      case "CONCEPT":
        return isNewPYPConceptsLabelEnabled()
          ? _.toLower(t("common:key_concepts_updated"))
          : _.toLower(t("common:CONCEPT"));
      case "SUBJECT":
        return _.toLower(t("common:subject_plural"));
      default:
        return _.toLower(groupLabel || t(`common:${groupId}`));
    }
  };

  const labelText = getLabelText();

  return isNewPYPConceptsLabelEnabled()
    ? t("unitPlan:add_specified_concepts_with_label", { label: labelText })
    : t("unitPlan:add_key_concepts_with_label", { label: labelText });
};

const RelatedConceptTable = props => {
  const {
    groupedValue: initialGroupedValue,
    groupedOptions: initialGroupedOptions,
    onAddClick,
    orgRelatedConceptSets,
    groupedRelatedConcepts,
    testId,
    mode,
    isCourseOverview,
  } = props;

  const { t } = useI18n(["unitPlan", "common"]);

  const [groupedValue, setGroupedValue] = useState(initialGroupedValue);
  const [groupedOptions, setGroupedOptions] = useState(initialGroupedOptions);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    setGroupedValue(initialGroupedValue);
    setGroupedOptions(initialGroupedOptions);
  }, [initialGroupedValue, initialGroupedOptions]);

  const updateInputField = ({ value, key }) => {
    setGroupedValue(prevGroupedValue => {
      const updatedValue = update(prevGroupedValue, {
        [key]: { $set: value },
      });

      onAddClick({
        orgRelatedConceptSets,
        currentGroupedOptions: groupedOptions,
        groupedOptions: initialGroupedOptions,
        groupedValue: updatedValue,
      });

      return updatedValue;
    });
  };

  const updateGroupedState = ({ value, key }) => {
    setGroupedValue(prevGroupedValue => {
      const updatedValue = update(prevGroupedValue, {
        [key]: {
          $apply: (arr = []) => [...arr, value],
        },
      });

      setGroupedOptions(prevGroupedOptions => {
        const updatedOptions = update(prevGroupedOptions, {
          [key]: {
            $apply: (arr = []) => [...arr, value],
          },
        });

        onAddClick({
          orgRelatedConceptSets,
          currentGroupedOptions: updatedOptions,
          groupedOptions: initialGroupedOptions,
          groupedValue: updatedValue,
        });

        return updatedOptions;
      });

      return updatedValue;
    });
  };

  // Create data and headers for each group dynamically
  const groupsData = {};
  const groupHeaders = {};

  _.forEach(groupedRelatedConcepts, group => {
    const { id, label, relatedConceptSets } = group;

    // Initialize empty array for this group
    groupsData[id] = [];

    // Create table headers for this group
    groupHeaders[id] = [
      {
        key: "concept",
        value: <div className="text-label">{getGroupLabel(id, t)}</div>,
        style: {
          width: "50%",
          whiteSpace: "normal",
        },
      },
      {
        key: "values",
        value: <div className="text-label">{getRelatedConceptsLabel(t)}</div>,
        style: { width: "50%", whiteSpace: "normal" },
      },
    ];

    if (!_.isEmpty(relatedConceptSets)) {
      // Add concept items to the group's data array
      _.forEach(relatedConceptSets, set => {
        const { id: conceptId, label: conceptLabel, isArchived = false } = set;
        const conceptValue = _.get(groupedValue, conceptId, []);
        const options = _.get(groupedOptions, conceptId, []);

        // Create the concept label cell
        const labelText = isArchived
          ? `${conceptLabel} (${t("common:archived")})`
          : conceptLabel;

        const conceptCell = {
          key: "concept",
          value: (
            <Tooltip
              tooltip={labelText}
              placement={"right"}
              showArrow={true}
              overlayInnerStyle={styles.overlayInnerStyle}
            >
              <div className={classes.conceptLabel}>
                <span className="text-body">{labelText}</span>
              </div>
            </Tooltip>
          ),
        };

        // Create the values cell
        let valuesCell;

        if (_.isEqual(mode, "view")) {
          const textValue = getFormattedLabels(conceptValue, t);
          const textValueClass = classNames({
            [classes.itemValue]: true,
            [classes.emptyItemValue]: _.isEmpty(conceptValue),
            "text-body": true,
          });
          const labels = getLabels(conceptValue);

          valuesCell = {
            key: "values",
            value: !_.isEqual(textValue, t("common:not_selected")) ? (
              <Tooltip
                tooltip={labels}
                placement={"right"}
                showArrow={true}
                overlayInnerStyle={styles.overlayInnerStyle}
              >
                <div className={textValueClass}>{textValue}</div>
              </Tooltip>
            ) : (
              <div className={textValueClass}>{textValue}</div>
            ),
          };
        } else {
          valuesCell = {
            key: "values",
            value: (
              <div className={classes.dropdownContainer}>
                <MultiSelectDropdown
                  variant={MultiSelectDropdown.Variants.OBJECT_VALUE_TAGS}
                  value={conceptValue}
                  options={options}
                  onChange={value =>
                    updateInputField({ value, key: conceptId })
                  }
                  placeholder={t("unitPlan:relatedConcept_dropdown_emptyText")}
                  isCreatable={true}
                  isClearable={true}
                  onCreateOption={val =>
                    updateGroupedState({
                      value: { value: val, label: val },
                      key: conceptId,
                    })
                  }
                  testId={`${testId}-${_.camelCase(conceptLabel)}`}
                  isSearchable={true}
                  showTag={true}
                  hideDropdownIndicator={false}
                  isCell={true}
                />
              </div>
            ),
            style: { padding: "0px" },
          };
        }

        // Add the row to this group's data array
        groupsData[id].push({
          id: conceptId,
          rowData: [conceptCell, valuesCell],
        });
      });
    }
  });

  // Get the group IDs for iterating
  const groupIds = _.keys(groupsData);

  const tableProps = {
    collapseWhenEmpty: false,
    size: mode == "view" ? "auto" : "medium",
    showBorder: true,
    isLoading: props.isLoading,
    selectable: false,
    dsVersion: "2.0",
    headerTheme: "subtle",
    showRowBackgroundOnHover: false,
    emptyState: (
      <div className={classes.emptyState}>{t("common:not_selected")}</div>
    ),
  };

  // Filter out empty groups in view mode
  const filteredGroupIds = _.isEqual(mode, "view")
    ? _.filter(groupIds, id => !_.isEmpty(groupsData[id]))
    : groupIds;

  // Show a single "Not defined" if all groups are empty in view mode
  if (_.isEmpty(filteredGroupIds) && _.isEqual(mode, "view")) {
    return (
      <span className={classNames("text-body", classes.emptyTextContainer)}>
        {t("common:not_defined")}
      </span>
    );
  }

  // Course overview
  if (isCourseOverview && _.isEqual(mode, "view")) {
    return (
      <div className={classes.courseOverviewContainer}>
        {_.map(groupIds, groupId => {
          const group = _.find(groupedRelatedConcepts, { id: groupId });
          const relatedConceptSets = _.get(group, "relatedConceptSets", []);

          const conceptsWithValues = _.filter(relatedConceptSets, set => {
            const conceptValue = _.get(groupedValue, set.id, []);
            return !_.isEmpty(conceptValue);
          });

          const hasNoValues = _.isEmpty(conceptsWithValues);

          return (
            <div key={groupId} className={classes.courseOverviewGroup}>
              <div
                className={classNames(
                  "text-label",
                  classes.courseOverviewGroupLabel
                )}
              >
                <span className={classNames(classes.bullet, "text-label")}>
                  •
                </span>
                {getGroupLabel(groupId, t)}
              </div>
              {hasNoValues ? (
                <div
                  className={classNames(
                    "text-body",
                    classes.courseOverviewEmptyState
                  )}
                >
                  {t("common:not_selected")}
                </div>
              ) : (
                <div className={classes.courseOverviewItems}>
                  {_.map(relatedConceptSets, set => {
                    const { id: conceptId, label: conceptLabel } = set;
                    const conceptValue = _.get(groupedValue, conceptId, []);

                    if (_.isEmpty(conceptValue)) return null;

                    const valuesText = _.map(conceptValue, "label").join(", ");

                    return (
                      <div
                        key={conceptId}
                        className={classes.courseOverviewItem}
                      >
                        <span
                          className={classNames(
                            classes.circleBullet,
                            "text-label"
                          )}
                        >
                          ◦
                        </span>
                        <span
                          className={classNames(
                            "text-label",
                            classes.courseOverviewItemLabel
                          )}
                        >
                          {conceptLabel}:
                        </span>
                        <span
                          className={classNames(
                            "text-body",
                            classes.courseOverviewItemValue
                          )}
                        >
                          {valuesText}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={classes.tablesContainer}>
      {_.map(groupIds, groupId => {
        const group = _.find(groupedRelatedConcepts, { id: groupId });
        const groupLabel = group?.label;
        const data = groupsData[groupId];
        const headers = groupHeaders[groupId];

        // Skip groups with no data in view mode
        if (_.isEmpty(data) && mode === "view") {
          return null;
        }

        return (
          <div key={groupId}>
            {_.isEmpty(data) && mode === "edit" ? (
              <div className={classNames("text-body", classes.emptyState)}>
                {getEmptyStateLabel(groupId, groupLabel, t)}
              </div>
            ) : (
              <div className={classes.tableContainer} ref={tableContainerRef}>
                <Table
                  headers={headers}
                  data={data}
                  testId={`${testId}-${groupId.toLowerCase()}`}
                  {...tableProps}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const memoizedGetGroupedValueOptions = _.memoize(
  getRelatedConceptsGroupedValueOptions,
  params => JSON.stringify(params)
);

const mapStateToProps = (state, ownProps) => {
  const { subjects, concepts } = ownProps;
  const curriculumId = getSelectedCurriculumId(state);
  const organizationId = getOrganizationId(state);
  return {
    concepts,
    subjects,
    isData: true,
    isLoading: false,
    organizationId,
    curriculumId,
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getPlannerElementNodeSetsFromIdsQuery, {
    name: "getPlannerElementNodeSetsFromIds",
    skip: ({ subjects, concepts, mode, plannerElementNodeSetId }) =>
      (_.isEmpty(subjects) && _.isEmpty(concepts)) ||
      !plannerElementNodeSetId ||
      mode === "view",
    options: ({ plannerElementNodeSetId, subjects, concepts }) => {
      const queryVariables = {
        plannerElementNodeSetIds: [plannerElementNodeSetId],
        plannerElementNodeFilters: {
          subjects,
          concepts,
        },
      };
      return {
        fetchPolicy: "cache-and-network",
        variables: queryVariables,
      };
    },
    props({ getPlannerElementNodeSetsFromIds }) {
      const queryData = _.get(
        getPlannerElementNodeSetsFromIds,
        "multiNode",
        {}
      );

      const relatedConceptSet = _.get(queryData, "[0]", {});
      return { getPlannerElementNodeSetsFromIds, relatedConceptSet, queryData };
    },
  }),
  withDerivedProps(
    ({
      getPlannerElementNodeSetsFromIds,
      relatedConceptSet,
      selectedRelatedConcepts,
      subjects,
      value,
      concepts,
      groupedOptionsFields,
      queryData,
      isData,
      isLoading,
      mode,
    }) => {
      const isViewMode = mode === "view";
      const orgRelatedConceptSets = isViewMode
        ? []
        : _.map(
            _.get(
              relatedConceptSet,
              "plannerElementNodeSetWrapperV2.publishedData.edges"
            ),
            "node"
          );
      const {
        groupedRelatedConcepts,
        groupedValue,
        groupedOptions,
      } = memoizedGetGroupedValueOptions({
        nodes: _.unionBy(
          [...orgRelatedConceptSets, ...selectedRelatedConcepts],
          "id"
        ),
        subjects,
        value,
        concepts,
        groupedOptionsFields,
      });

      return {
        isData: (!_.isEmpty(queryData) || isViewMode) && isData,
        groupedRelatedConcepts,
        groupedOptions,
        groupedValue,
        orgRelatedConceptSets,
        isLoading:
          [1, 2].includes(getPlannerElementNodeSetsFromIds?.networkStatus) ||
          isLoading,
      };
    }
  )
)(RelatedConceptTable);
